import { createContext, useContext, useEffect, useState, type FC, type ReactNode } from 'react';
import type { GameSystemSlug } from '@/types';

/**
 * Available user theme preferences
 */
export type UserTheme = 'grim-dark' | 'bright-mode';

/**
 * Available faction slugs
 */
export type FactionSlug =
  | 'space-marines'
  | 'necrons'
  | 'dark-angels'
  | 'blood-angels'
  | 'orks'
  | 'stormcast';

/**
 * Font scale levels for accessibility
 */
export type FontScale = 'small' | 'normal' | 'large' | 'extra-large';

/**
 * Accessibility settings
 */
interface AccessibilitySettings {
  /** Font scale level */
  fontScale: FontScale;
  /** Reduce motion (disable animations) */
  reduceMotion: boolean;
  /** High contrast mode */
  highContrast: boolean;
  /** Sound effects enabled */
  soundEnabled: boolean;
  /** Sound volume (0-100) */
  soundVolume: number;
}

/**
 * Theme context value interface
 */
interface ThemeContextValue {
  /** Current user theme (grim-dark or bright-mode) */
  userTheme: UserTheme;
  /** Current game system */
  game: GameSystemSlug;
  /** Current faction (optional) */
  faction: FactionSlug | null;
  /** Accessibility settings */
  accessibility: AccessibilitySettings;
  /** Set the user theme preference */
  setUserTheme: (theme: UserTheme) => void;
  /** Set the game system theme */
  setGame: (game: GameSystemSlug) => void;
  /** Set the faction theme */
  setFaction: (faction: FactionSlug | null) => void;
  /** Set accessibility settings */
  setAccessibility: (settings: Partial<AccessibilitySettings>) => void;
}

/**
 * Theme context props
 */
interface ThemeProviderProps {
  children: ReactNode;
  /** Default user theme */
  defaultUserTheme?: UserTheme;
  /** Default game system */
  defaultGame?: GameSystemSlug;
  /** Default faction */
  defaultFaction?: FactionSlug | null;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Default accessibility settings
 */
const defaultAccessibility: AccessibilitySettings = {
  fontScale: 'normal',
  reduceMotion: false,
  highContrast: false,
  soundEnabled: true,
  soundVolume: 50,
};

/** Stored theme to supported theme. The retired 'dark-mode' (and anything unknown) reads as the dark default. */
export function normaliseStoredTheme(value: string | null): UserTheme {
  return value === 'bright-mode' ? 'bright-mode' : 'grim-dark';
}

/**
 * Load theme from localStorage with fallback
 */
function loadStoredTheme(): UserTheme {
  try {
    return normaliseStoredTheme(localStorage.getItem('gdc-user-theme'));
  } catch {
    return 'grim-dark';
  }
}

/**
 * Load accessibility settings from localStorage
 */
function loadStoredAccessibility(): AccessibilitySettings {
  try {
    const stored = localStorage.getItem('gdc-accessibility');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        ...defaultAccessibility,
        ...parsed,
      };
    }
  } catch {
    // Ignore parse errors
  }
  return defaultAccessibility;
}

/**
 * ThemeProvider - Manages user theme, game, faction, and accessibility settings
 *
 * Applies data-theme, data-game, data-faction, and accessibility data attributes
 * to document.documentElement which trigger CSS custom property overrides.
 */
export const ThemeProvider: FC<ThemeProviderProps> = ({
  children,
  defaultUserTheme,
  defaultGame = 'wh40k',
  defaultFaction = null,
}) => {
  const [userTheme, setUserTheme] = useState<UserTheme>(() =>
    defaultUserTheme ?? loadStoredTheme()
  );
  const [game, setGame] = useState<GameSystemSlug>(defaultGame);
  const [faction, setFaction] = useState<FactionSlug | null>(defaultFaction);
  const [accessibility, setAccessibilityState] = useState<AccessibilitySettings>(loadStoredAccessibility);

  // Apply theme to document element
  useEffect(() => {
    const root = document.documentElement;

    // Set user theme (grim-dark or bright-mode)
    root.dataset.theme = userTheme;

    // Set game theme
    root.dataset.game = game;

    // Set faction theme if present, otherwise remove attribute
    if (faction) {
      root.dataset.faction = faction;
    } else {
      delete root.dataset.faction;
    }
  }, [userTheme, game, faction]);

  // Apply accessibility settings to document element
  useEffect(() => {
    const root = document.documentElement;

    // Font scale
    root.dataset.fontScale = accessibility.fontScale;

    // Reduce motion
    if (accessibility.reduceMotion) {
      root.dataset.reduceMotion = 'true';
    } else {
      delete root.dataset.reduceMotion;
    }

    // High contrast
    if (accessibility.highContrast) {
      root.dataset.highContrast = 'true';
    } else {
      delete root.dataset.highContrast;
    }
  }, [accessibility.fontScale, accessibility.reduceMotion, accessibility.highContrast]);

  // Persist theme to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('gdc-user-theme', userTheme);
      localStorage.setItem('gdc-theme-game', game);
      if (faction) {
        localStorage.setItem('gdc-theme-faction', faction);
      } else {
        localStorage.removeItem('gdc-theme-faction');
      }
    } catch (error) {
      console.warn('Failed to persist theme to localStorage:', error);
    }
  }, [userTheme, game, faction]);

  // Persist accessibility settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('gdc-accessibility', JSON.stringify(accessibility));
    } catch (error) {
      console.warn('Failed to persist accessibility to localStorage:', error);
    }
  }, [accessibility]);

  // Helper to update accessibility settings
  const setAccessibility = (settings: Partial<AccessibilitySettings>) => {
    setAccessibilityState((prev) => ({
      ...prev,
      ...settings,
    }));
  };

  const value: ThemeContextValue = {
    userTheme,
    game,
    faction,
    accessibility,
    setUserTheme,
    setGame,
    setFaction,
    setAccessibility,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * useTheme - Hook to access theme context
 *
 * @returns Theme context value with game, faction, accessibility, and setters
 * @throws Error if used outside ThemeProvider
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};

/**
 * useOptionalTheme - like useTheme, but returns undefined outside a ThemeProvider instead of
 * throwing, for components that only read a setting (such as the sound switch) and must still render in isolation.
 */
export const useOptionalTheme = (): ThemeContextValue | undefined => useContext(ThemeContext);
